import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft, ClipboardList, Download, Send, Plus, Pencil, Trash2,
  LogOut, LogIn, BedDouble, Wrench, RefreshCw, FileDown,
  ClipboardCheck, Repeat, CheckCircle2, Circle,
} from "lucide-react";
import { toast } from "sonner";
import { todayLocalIso, addDaysIso, todayInOrgIso } from "@/lib/dateUtils";

const PRIO_BADGE = {
  low: "bg-slate-100 text-slate-700 border-slate-200",
  normal: "bg-blue-100 text-blue-800 border-blue-200",
  high: "bg-amber-100 text-amber-900 border-amber-300",
  urgent: "bg-red-100 text-red-800 border-red-300",
};
const STATUS_BADGE = {
  open: "bg-red-100 text-red-700 border-red-200",
  in_progress: "bg-amber-100 text-amber-900 border-amber-300",
  done: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

export default function Worksheet() {
  const navigate = useNavigate();
  const { orgTimezone } = useAuth();
  const [date, setDate] = useState(() => todayInOrgIso(orgTimezone));
  const [data, setData] = useState(null);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTask, setEditingTask] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendRecipients, setSendRecipients] = useState("");
  const [sendNote, setSendNote] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ws, un] = await Promise.all([
        api.get(`/housing/worksheet?date=${date}`),
        api.get("/housing/units"),
      ]);
      setData(ws.data);
      setUnits(un.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load worksheet");
    } finally { setLoading(false); }
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const niceDate = useMemo(() => {
    const d = new Date(date + "T12:00:00");
    return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  }, [date]);

  const shiftDay = (delta) => {
    setDate(addDaysIso(date, delta));
  };

  const saveTask = async () => {
    if (!editingTask.title?.trim()) { toast.error("Title is required"); return; }
    try {
      if (editingTask.id) {
        await api.put(`/housing/maintenance/${editingTask.id}`, editingTask);
      } else {
        await api.post("/housing/maintenance", editingTask);
      }
      toast.success("Repair saved");
      setEditingTask(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const deleteTask = async (t) => {
    if (!window.confirm(`Delete repair '${t.title}'?`)) return;
    try {
      await api.delete(`/housing/maintenance/${t.id}`);
      toast.success("Repair deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  const downloadWorkOrder = async (t) => {
    try {
      const r = await api.get(`/housing/maintenance/${t.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `work_order_${t.title.replace(/\s+/g, "_").slice(0, 30)}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Work order downloaded");
    } catch { toast.error("Download failed"); }
  };

  const emailVendor = async (t) => {
    if (!t.assigned_vendor_email) {
      const ok = window.confirm(`No vendor email assigned. Continue and pick a recipient now?`);
      if (!ok) return;
      const recs = window.prompt("Recipient email(s), comma-separated:");
      if (!recs) return;
      const note = window.prompt("Optional note for the vendor:") || "";
      try {
        const { data } = await api.post(`/housing/maintenance/${t.id}/email-vendor`, {
          recipients: recs.split(/[,;]/).map(s => s.trim()).filter(Boolean), note,
        });
        toast.success(data.mocked ? `Mocked send to ${data.recipients.join(", ")}` : `Emailed ${data.recipients.join(", ")}`);
        load();
      } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
      return;
    }
    if (!window.confirm(`Email this work order to ${t.assigned_vendor_name || t.assigned_vendor_email}?`)) return;
    try {
      const { data } = await api.post(`/housing/maintenance/${t.id}/email-vendor`, { recipients: [], note: "" });
      toast.success(data.mocked ? `Mocked send (RESEND_API_KEY missing)` : `Emailed ${data.recipients.join(", ")}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
  };

  const quickStatus = async (t, status) => {
    try {
      await api.put(`/housing/maintenance/${t.id}`, { ...t, status });
      toast.success(`Marked ${status.replace("_", " ")}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/housing/worksheet/pdf?date=${date}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `worksheet_${date}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Worksheet downloaded");
    } catch { toast.error("Download failed"); }
  };

  const sendWorksheet = async () => {
    const recs = sendRecipients.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
    if (recs.length === 0) { toast.error("At least one email address required"); return; }
    setSending(true);
    try {
      const { data: res } = await api.post("/housing/worksheet/send", {
        date, recipients: recs, note: sendNote,
      });
      if (res.mocked) {
        toast.info(`Send mocked (RESEND_API_KEY missing) — would go to ${res.recipients.join(", ")}`, { duration: 5000 });
      } else {
        toast.success(`Sent to ${res.recipients.join(", ")}`);
      }
      setSendOpen(false);
      setSendRecipients("");
      setSendNote("");
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  if (loading) return <div className="p-6 text-[#7A8A84]">Loading worksheet…</div>;
  if (!data) return null;
  const s = data.summary;

  return (
    <div className="space-y-6" data-testid="worksheet-page">
      <Button variant="ghost" onClick={() => navigate(-1)} className="text-[#7A8A84]" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1C1F1E] flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-[#40916C]" />
            Daily Housekeeping Worksheet
          </h1>
          <p className="text-sm text-[#7A8A84]">{niceDate}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => shiftDay(-1)} data-testid="prev-day-btn">← Prev</Button>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-40"
            data-testid="worksheet-date-input"
          />
          <Button variant="outline" size="sm" onClick={() => setDate(todayInOrgIso(orgTimezone))} data-testid="today-btn">Today</Button>
          <Button variant="outline" size="sm" onClick={() => shiftDay(1)} data-testid="next-day-btn">Next →</Button>
          <Button size="sm" onClick={downloadPdf} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="download-pdf-btn">
            <Download className="h-3.5 w-3.5 mr-1" /> Download PDF
          </Button>
          <Button size="sm" onClick={() => setSendOpen(true)} className="bg-[#40916C] hover:bg-[#2D6A4F]" data-testid="send-worksheet-btn">
            <Send className="h-3.5 w-3.5 mr-1" /> Email
          </Button>
          <Button size="sm" variant="outline" onClick={load} data-testid="refresh-btn">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="summary-row">
        <StatCard label="Check-outs" value={s.checkouts} color="#B5392F" testId="stat-checkouts" />
        <StatCard label="Check-ins" value={s.checkins} color="#2E7D5B" testId="stat-checkins" />
        <StatCard label="Turnovers" value={s.turnovers} color="#B87D07" testId="stat-turnovers" sub={s.same_day_turnover ? `${s.same_day_turnover} same-day` : ""} />
        <StatCard label="Occupied" value={s.occupied} color="#0F4C81" testId="stat-occupied" />
        <StatCard label="Helper tasks" value={s.helper_tasks || 0} color="#1F6FB0" testId="stat-helper-tasks" sub={s.helper_tasks_pending ? `${s.helper_tasks_pending} pending` : ""} />
        <StatCard label="Open repairs" value={s.open_repairs} color="#B5392F" testId="stat-open-repairs" />
        <StatCard label="In progress" value={s.in_progress_repairs} color="#B87D07" testId="stat-in-progress" />
        <StatCard label="Units" value={units.filter((u) => u.active).length} color="#525C58" testId="stat-units" />
      </div>

      {/* Check-outs */}
      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <LogOut className="h-4 w-4 text-red-600" /> Check-outs today ({data.checkouts.length})
            <span className="text-xs text-[#7A8A84] font-normal">· these units need turnover cleaning</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <WorksheetTable
            rows={data.checkouts}
            empty="No check-outs today."
            testPrefix="checkout"
            sameDayTurnoverIds={data.same_day_turnover_unit_ids}
          />
        </CardContent>
      </Card>

      {/* Check-ins */}
      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <LogIn className="h-4 w-4 text-emerald-600" /> Check-ins today ({data.checkins.length})
            <span className="text-xs text-[#7A8A84] font-normal">· rooms to prepare</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <WorksheetTable
            rows={data.checkins}
            empty="No check-ins today."
            testPrefix="checkin"
            sameDayTurnoverIds={data.same_day_turnover_unit_ids}
          />
        </CardContent>
      </Card>

      {/* Occupied */}
      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BedDouble className="h-4 w-4 text-blue-600" /> Occupied today ({data.occupied.length})
            <span className="text-xs text-[#7A8A84] font-normal">· no change needed</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.occupied.length === 0 ? (
            <p className="text-sm text-[#7A8A84] text-center py-6">No mid-stay occupancy today.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Unit</TableHead><TableHead>Visitor</TableHead>
                    <TableHead>Party</TableHead><TableHead>Shared</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.occupied.map((r, idx) => (
                    <TableRow key={`${r.booking_id}-${r.unit_id}-${idx}`} data-testid={`occupied-row-${idx}`}>
                      <TableCell className="font-medium">{r.unit_name}</TableCell>
                      <TableCell>{r.visitor_name}</TableCell>
                      <TableCell>{r.party_size}</TableCell>
                      <TableCell>{r.shared_room ? <Badge className="bg-purple-100 text-purple-800 border-purple-300">Shared</Badge> : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Helper tasks today */}
      <Card className="border-[#E8E8E6]" data-testid="worksheet-helper-tasks">
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
            <span className="flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4 text-blue-700" />
              Helper tasks today ({(data.helper_tasks || []).length})
              <span className="text-xs text-[#7A8A84] font-normal">· ad-hoc and recurring chores</span>
            </span>
            <Button size="sm" variant="outline" onClick={() => navigate("/helper-tasks")} className="border-[#1B4332]/20 text-[#1B4332]" data-testid="open-helper-tasks">
              <Plus className="h-3.5 w-3.5 mr-1" /> Manage tasks
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(data.helper_tasks || []).length === 0 ? (
            <p className="text-sm text-[#7A8A84] text-center py-6">No helper tasks scheduled for this day.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task</TableHead>
                    <TableHead>Helper</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>When</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data.helper_tasks || []).map((t, idx) => {
                    const done = t.occurrence_status === "done";
                    const toggleDone = async () => {
                      try {
                        await api.put(`/housing/helper-tasks/${t.task_id}/status?status=${done ? "pending" : "done"}${t.kind === "recurring" ? `&occurrence_date=${t.date}` : ""}`);
                        load();
                      } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
                    };
                    return (
                      <TableRow key={`${t.task_id}-${idx}`} data-testid={`helper-task-row-${t.task_id}`} className={done ? "bg-emerald-50/50" : ""}>
                        <TableCell>
                          <div className="flex items-start gap-2">
                            {t.kind === "recurring" ? <Repeat className="h-3.5 w-3.5 mt-0.5 text-purple-700 shrink-0" /> : <ClipboardCheck className="h-3.5 w-3.5 mt-0.5 text-blue-700 shrink-0" />}
                            <div className="min-w-0">
                              <p className={`font-medium ${done ? "line-through text-[#7A8A84]" : "text-[#1C1F1E]"}`}>{t.title}</p>
                              {t.description && <p className="text-[11px] text-[#7A8A84] line-clamp-2">{t.description}</p>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{t.helper_name || <span className="text-amber-700">Unassigned</span>}</TableCell>
                        <TableCell className="text-sm text-[#525C58]">{t.unit_name || "—"}</TableCell>
                        <TableCell className="text-xs text-[#525C58]">{t.start_time || "—"}{t.end_time ? ` → ${t.end_time}` : ""}</TableCell>
                        <TableCell><Badge className={`${PRIO_BADGE[t.priority] || PRIO_BADGE.normal} border text-[10px]`}>{t.priority}</Badge></TableCell>
                        <TableCell>
                          <Badge className={`${done ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-200"} border text-[10px] inline-flex items-center gap-1`}>
                            {done ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
                            {done ? "done" : t.occurrence_status || "pending"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={toggleDone} className={done ? "text-[#7A8A84]" : "text-emerald-700 hover:bg-emerald-50"} data-testid={`helper-task-toggle-${t.task_id}`}>
                            <CheckCircle2 className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Repairs */}
      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
            <span className="flex items-center gap-2">
              <Wrench className="h-4 w-4 text-amber-600" />
              Repairs & maintenance ({data.repairs.length})
            </span>
            <Button size="sm" onClick={() => setEditingTask({ title: "", description: "", priority: "normal", status: "open", unit_id: "", due_date: "" })}
              className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="add-repair-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add repair
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.repairs.length === 0 ? (
            <p className="text-sm text-[#7A8A84] text-center py-6">No open repairs — nice work!</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Unit</TableHead><TableHead>Title</TableHead>
                    <TableHead>Priority</TableHead><TableHead>Status</TableHead>
                    <TableHead>Due</TableHead><TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.repairs.map((r) => (
                    <TableRow key={r.id} data-testid={`repair-row-${r.id}`}>
                      <TableCell className="font-medium">{r.unit_name}</TableCell>
                      <TableCell>
                        <div className="font-medium">{r.title}</div>
                        {r.description && <p className="text-[11px] text-[#7A8A84] truncate max-w-[300px]">{r.description}</p>}
                      </TableCell>
                      <TableCell><Badge className={PRIO_BADGE[r.priority] || PRIO_BADGE.normal}>{r.priority}</Badge></TableCell>
                      <TableCell><Badge className={STATUS_BADGE[r.status] || STATUS_BADGE.open}>{r.status.replace("_", " ")}</Badge></TableCell>
                      <TableCell className="text-sm">{r.due_date || "—"}</TableCell>
                      <TableCell className="text-right space-x-1">
                        {r.status !== "done" && (
                          <Button size="sm" variant="ghost" onClick={() => quickStatus(r, r.status === "open" ? "in_progress" : "done")}
                            className="text-emerald-700" data-testid={`advance-${r.id}`}>
                            {r.status === "open" ? "Start" : "Done"}
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => downloadWorkOrder(r)} className="text-[#1B4332]" title="Download work order PDF" data-testid={`download-wo-${r.id}`}>
                          <FileDown className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => emailVendor(r)} className="text-emerald-700" title="Email vendor" data-testid={`email-vendor-${r.id}`}>
                          <Send className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingTask({ ...r })} className="text-[#1B4332]" data-testid={`edit-repair-${r.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteTask(r)} className="text-red-600" data-testid={`delete-repair-${r.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Repair edit dialog */}
      <Dialog open={!!editingTask} onOpenChange={(o) => !o && setEditingTask(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTask?.id ? "Edit repair" : "Add repair"}</DialogTitle>
            <DialogDescription>Track maintenance and repairs on the daily worksheet.</DialogDescription>
          </DialogHeader>
          {editingTask && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Title *</Label>
                <Input value={editingTask.title} onChange={(e) => setEditingTask({ ...editingTask, title: e.target.value })}
                  data-testid="repair-title-input" placeholder="e.g. Fix leaky tap in master bath" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Unit</Label>
                  <Select value={editingTask.unit_id || "__general__"} onValueChange={(v) => setEditingTask({ ...editingTask, unit_id: v === "__general__" ? null : v })}>
                    <SelectTrigger data-testid="repair-unit-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__general__">General / site-wide</SelectItem>
                      {units.filter((u) => u.active).map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Priority</Label>
                  <Select value={editingTask.priority} onValueChange={(v) => setEditingTask({ ...editingTask, priority: v })}>
                    <SelectTrigger data-testid="repair-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Status</Label>
                  <Select value={editingTask.status} onValueChange={(v) => setEditingTask({ ...editingTask, status: v })}>
                    <SelectTrigger data-testid="repair-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In progress</SelectItem>
                      <SelectItem value="done">Done</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Due date</Label>
                  <Input type="date" value={editingTask.due_date || ""} onChange={(e) => setEditingTask({ ...editingTask, due_date: e.target.value })} data-testid="repair-due-input" />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Description</Label>
                <Textarea rows={3} value={editingTask.description || ""} onChange={(e) => setEditingTask({ ...editingTask, description: e.target.value })} data-testid="repair-description-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Estimated cost</Label>
                  <Input type="number" value={editingTask.cost || 0} onChange={(e) => setEditingTask({ ...editingTask, cost: Number(e.target.value) })} data-testid="repair-cost-input" />
                </div>
                <div className="space-y-1">
                  <Label>Currency</Label>
                  <Select value={editingTask.cost_currency || "KES"} onValueChange={(v) => setEditingTask({ ...editingTask, cost_currency: v })}>
                    <SelectTrigger data-testid="repair-cost-currency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="KES">KES</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1 pt-2 border-t border-[#E8E8E6]">
                <Label className="text-[11px] uppercase tracking-wide text-[#7A8A84]">Vendor / contractor (for emailing the work order)</Label>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>Name</Label>
                  <Input value={editingTask.assigned_vendor_name || ""} onChange={(e) => setEditingTask({ ...editingTask, assigned_vendor_name: e.target.value })} data-testid="repair-vendor-name-input" />
                </div>
                <div className="space-y-1">
                  <Label>Email</Label>
                  <Input type="email" value={editingTask.assigned_vendor_email || ""} onChange={(e) => setEditingTask({ ...editingTask, assigned_vendor_email: e.target.value })} data-testid="repair-vendor-email-input" />
                </div>
                <div className="space-y-1">
                  <Label>Phone</Label>
                  <Input value={editingTask.assigned_vendor_phone || ""} onChange={(e) => setEditingTask({ ...editingTask, assigned_vendor_phone: e.target.value })} data-testid="repair-vendor-phone-input" />
                </div>
              </div>
              {editingTask.email_log && editingTask.email_log.length > 0 && (
                <div className="text-[11px] text-[#7A8A84] border-t border-[#E8E8E6] pt-2">
                  Last emailed {editingTask.email_log[editingTask.email_log.length - 1].sent_at?.slice(0, 16).replace("T", " ")} by {editingTask.email_log[editingTask.email_log.length - 1].sent_by}
                  ({editingTask.email_log.length} send{editingTask.email_log.length === 1 ? "" : "s"} on file)
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTask(null)} data-testid="repair-cancel-btn">Cancel</Button>
            <Button onClick={saveTask} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="repair-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send email dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Email worksheet</DialogTitle>
            <DialogDescription>Send the PDF worksheet to one or more team members.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Recipients (comma-separated)</Label>
              <Textarea rows={2} value={sendRecipients} onChange={(e) => setSendRecipients(e.target.value)}
                placeholder="housekeeping@hospital.org, cleaner1@hospital.org" data-testid="send-recipients-input" />
            </div>
            <div className="space-y-1">
              <Label>Note (optional)</Label>
              <Textarea rows={3} value={sendNote} onChange={(e) => setSendNote(e.target.value)}
                placeholder="Good morning team! Please prioritise room turnovers today." data-testid="send-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)} data-testid="send-cancel-btn">Cancel</Button>
            <Button onClick={sendWorksheet} disabled={sending} className="bg-[#40916C] hover:bg-[#2D6A4F]" data-testid="send-confirm-btn">
              {sending ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, color, sub, testId }) {
  return (
    <div className="rounded-lg border border-[#E8E8E6] bg-white p-3" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-[#7A8A84] font-semibold">{label}</p>
      <p className="text-2xl font-bold mt-1" style={{ color }}>{value}</p>
      {sub && <p className="text-[10px] text-[#7A8A84] mt-0.5">{sub}</p>}
    </div>
  );
}

function WorksheetTable({ rows, empty, testPrefix, sameDayTurnoverIds }) {
  if (rows.length === 0) return <p className="text-sm text-[#7A8A84] text-center py-6">{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Unit</TableHead><TableHead>Visitor</TableHead>
            <TableHead>Party</TableHead><TableHead>Cleaner</TableHead><TableHead>Notes</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, idx) => {
            const sameDay = sameDayTurnoverIds.includes(r.unit_id);
            return (
              <TableRow key={`${r.booking_id}-${r.unit_id}-${idx}`} data-testid={`${testPrefix}-row-${idx}`} className={sameDay ? "bg-amber-50/50" : ""}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2 flex-wrap">
                    {r.unit_name}
                    {sameDay && <Badge className="bg-amber-100 text-amber-900 border-amber-300 text-[9px] uppercase">Same-day turnover</Badge>}
                  </div>
                </TableCell>
                <TableCell>{r.visitor_name}</TableCell>
                <TableCell>{r.party_size}</TableCell>
                <TableCell>{r.cleaner_name || <span className="text-[#7A8A84]">—</span>}</TableCell>
                <TableCell className="text-sm text-[#525C58]">{r.notes || "—"}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
